import { useEffect } from "react";
import { App, Button, Card, Col, Divider, Form, Image, Input, Row, Space, Switch, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HomeBanner, HomeFeature } from "@glow-price/shared";

import { homeSettingsApi } from "../api/resources";
import { ImageUpload } from "../components/ImageUpload";

const BANNER_VARIANTS = [
  { name: "酒红", bg: "linear-gradient(135deg, #4a1c2a 0%, #6d2839 100%)" },
  { name: "炭墨", bg: "linear-gradient(135deg, #2a2520 0%, #4a3f33 100%)" },
  { name: "酒红→金", bg: "linear-gradient(135deg, #6d2839 0%, #b8956b 100%)" },
];

type BannersForm = { items: HomeBanner[] };

export function HomeEditorialPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<HomeFeature>();
  const [bannerForm] = Form.useForm<BannersForm>();

  const featureQuery = useQuery({
    queryKey: ["home-feature"],
    queryFn: () => homeSettingsApi.getFeature(),
  });
  const bannersQuery = useQuery({
    queryKey: ["home-banners"],
    queryFn: () => homeSettingsApi.getBanners(),
  });

  useEffect(() => {
    if (featureQuery.data) form.setFieldsValue(featureQuery.data);
  }, [featureQuery.data, form]);
  useEffect(() => {
    if (bannersQuery.data) bannerForm.setFieldsValue({ items: bannersQuery.data.items });
  }, [bannersQuery.data, bannerForm]);

  const saveMutation = useMutation({
    mutationFn: (values: HomeFeature) => homeSettingsApi.putFeature(values),
    onSuccess: () => {
      message.success("已保存，H5 / 小程序首页下次进入即生效");
      qc.invalidateQueries({ queryKey: ["home-feature"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const saveBannersMutation = useMutation({
    mutationFn: (items: HomeBanner[]) => homeSettingsApi.putBanners(items),
    onSuccess: () => {
      message.success("Banner 已保存");
      qc.invalidateQueries({ queryKey: ["home-banners"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const preview = Form.useWatch([], form) ?? featureQuery.data;
  const bannerPreview = (Form.useWatch([], bannerForm) as BannersForm | undefined)?.items ??
    bannersQuery.data?.items ?? [];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        首页运营管理
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        控制首页"本周编辑精选"大图运营卡的文案与图片。关闭后前端隐藏该卡片。
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={14}>
          <Card title="编辑精选大图卡" loading={featureQuery.isLoading}>
            <Form<HomeFeature>
              form={form}
              layout="vertical"
              onFinish={(values) => saveMutation.mutate(values)}
              initialValues={featureQuery.data}
            >
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="eyebrow"
                label="英文小标 (Eyebrow)"
                rules={[{ required: true, max: 40 }]}
                tooltip="大写全角字母效果，限 40 字以内"
              >
                <Input placeholder="Editor's Pick" />
              </Form.Item>
              <Form.Item
                name="title"
                label="标题"
                rules={[{ required: true, max: 60 }]}
              >
                <Input placeholder="本周编辑精选" />
              </Form.Item>
              <Form.Item
                name="body"
                label="副文案"
                rules={[{ required: true, max: 160 }]}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="奢护肌底新品 + 经典香氛特惠，由编辑组合呈现。"
                />
              </Form.Item>
              <Form.Item
                name="cta"
                label="按钮文案"
                rules={[{ required: true, max: 20 }]}
              >
                <Input placeholder="探索精选" />
              </Form.Item>
              <Form.Item
                name="linkTo"
                label="点击跳转路径"
                rules={[{ required: true, max: 120 }]}
                tooltip="H5 路由，例如 /top-deals 或 /search?q=明星单品"
              >
                <Input placeholder="/top-deals" />
              </Form.Item>
              <Form.Item
                name="imageUrl"
                label="背景图"
                rules={[{ required: true, type: "url" }]}
              >
                <ImageUpload folder="home-feature" width={120} height={80} />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saveMutation.isPending}
                >
                  保存
                </Button>
                <Button
                  onClick={() => form.resetFields()}
                  disabled={saveMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card title="实时预览" loading={featureQuery.isLoading}>
            {preview && <FeaturePreview value={preview as HomeFeature} />}
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 12, fontSize: 12, marginBottom: 0 }}
            >
              预览仅近似前端样式；最终效果以 H5 / 小程序为准。
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Typography.Title level={5} style={{ marginTop: 0 }}>
        顶部 Banner（3 张轮播）
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        渐变背景按槽位固定（酒红 / 炭墨 / 酒红→金），文案与跳转可改。
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={14}>
          <Card loading={bannersQuery.isLoading}>
            <Form<BannersForm>
              form={bannerForm}
              layout="vertical"
              onFinish={(values) => saveBannersMutation.mutate(values.items)}
              initialValues={bannersQuery.data}
            >
              {[0, 1, 2].map((idx) => (
                <Card
                  key={idx}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={
                    <span>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          background: BANNER_VARIANTS[idx]!.bg,
                          marginRight: 8,
                          verticalAlign: "middle",
                        }}
                      />
                      槽 #{idx + 1}（{BANNER_VARIANTS[idx]!.name}）
                    </span>
                  }
                >
                  <Form.Item
                    name={["items", idx, "title"]}
                    label="标题"
                    rules={[{ required: true, max: 30 }]}
                  >
                    <Input placeholder="今日好价" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "sub"]}
                    label="副文案"
                    rules={[{ required: true, max: 60 }]}
                  >
                    <Input placeholder="中免日上 · 多渠道实时比价" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "linkTo"]}
                    label="跳转路径"
                    rules={[{ required: true, max: 120 }]}
                  >
                    <Input placeholder="/top-deals" />
                  </Form.Item>
                </Card>
              ))}
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saveBannersMutation.isPending}
                >
                  保存 3 张 Banner
                </Button>
                <Button
                  onClick={() => bannerForm.resetFields()}
                  disabled={saveBannersMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card title="实时预览" loading={bannersQuery.isLoading}>
            {bannerPreview.map((b, idx) => (
              <BannerPreview key={idx} value={b} variant={BANNER_VARIANTS[idx]!} />
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

function BannerPreview({
  value,
  variant,
}: {
  value: HomeBanner;
  variant: { name: string; bg: string };
}) {
  return (
    <div
      style={{
        position: "relative",
        height: 90,
        padding: "16px 20px",
        marginBottom: 8,
        borderRadius: 10,
        background: variant.bg,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        letterSpacing: 0.4,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500 }}>{value?.title}</div>
      <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>{value?.sub}</div>
    </div>
  );
}

function FeaturePreview({ value }: { value: HomeFeature }) {
  return (
    <div
      style={{
        position: "relative",
        height: 220,
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e8e2dc",
        boxShadow: "0 4px 16px rgba(20,10,5,0.08)",
        opacity: value.enabled ? 1 : 0.4,
      }}
    >
      {value.imageUrl ? (
        <Image
          src={value.imageUrl}
          preview={false}
          width="100%"
          height={220}
          style={{ objectFit: "cover", display: "block" }}
        />
      ) : (
        <div style={{ width: "100%", height: 220, background: "#e8e2dc" }} />
      )}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(74,28,42,0.55) 0%, rgba(26,26,26,0.25) 55%, rgba(26,26,26,0.65) 100%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          padding: "22px 22px 20px",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              color: "#f0e7d8",
              opacity: 0.9,
            }}
          >
            {value.eyebrow}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 500,
              marginTop: 6,
              letterSpacing: 0.8,
              lineHeight: 1.25,
              maxWidth: 240,
            }}
          >
            {value.title}
          </div>
          <div
            style={{
              fontSize: 12,
              opacity: 0.85,
              marginTop: 8,
              letterSpacing: 0.3,
              maxWidth: 260,
              lineHeight: 1.5,
            }}
          >
            {value.body}
          </div>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            alignSelf: "flex-start",
            fontSize: 12,
            letterSpacing: 0.8,
            padding: "6px 12px",
            border: "1px solid rgba(240,231,216,0.6)",
            borderRadius: 999,
            color: "#f0e7d8",
            background: "rgba(0,0,0,0.15)",
          }}
        >
          {value.cta} →
        </div>
      </div>
    </div>
  );
}
